import { Injectable } from '@angular/core';
import {Chart} from 'angular-highcharts';

@Injectable({
  providedIn: 'root'
})
export class ChartTiktokService {

  // ── Shared modern theme pieces ──────────────────────────────────────
  private static readonly fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  private static baseTooltip(): any {
    return {
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderWidth: 0,
      borderRadius: 12,
      shadow: {
        offsetX: 0,
        offsetY: 4,
        width: 16,
        color: 'rgba(0,0,0,0.08)'
      },
      style: {
        fontSize: '13px',
        fontFamily: ChartTiktokService.fontFamily,
        color: '#1e293b'
      },
      padding: 12,
      useHTML: true
    };
  }

  private static baseLegend(): any {
    return {
      itemStyle: {
        color: '#64748b',
        fontSize: '13px',
        fontWeight: '500',
        fontFamily: ChartTiktokService.fontFamily
      },
      itemHoverStyle: {
        color: '#1e293b'
      },
      symbolRadius: 4
    };
  }

  private static baseChart(type: string, extra: any = {}): any {
    return {
      type,
      style: { fontFamily: ChartTiktokService.fontFamily },
      backgroundColor: 'transparent',
      spacing: [10, 10, 10, 10],
      animation: {
        duration: 800,
        easing: 'easeOutQuart'
      },
      ...extra
    };
  }

  private static modernXAxis(categories: any[], extra: any = {}): any {
    return {
      categories,
      title: { text: null },
      labels: {
        style: {
          color: '#94a3b8',
          fontSize: '12px',
          fontFamily: ChartTiktokService.fontFamily
        }
      },
      lineColor: '#e2e8f0',
      tickColor: 'transparent',
      ...extra
    };
  }

  private static modernYAxis(extra: any = {}): any {
    return {
      min: 0,
      title: { text: undefined },
      labels: {
        style: {
          color: '#94a3b8',
          fontSize: '12px',
          fontFamily: ChartTiktokService.fontFamily
        },
        overflow: 'justify'
      },
      gridLineColor: '#f1f5f9',
      gridLineDashStyle: 'Dash',
      ...extra
    };
  }

  private static showLoadingOnEmpty(): any {
    return {
      load: function () {
        const sumCount = this.series[0].data.reduce((sum, current) => sum + (current.y || current.options?.y || 0), 0);
        if (!sumCount) {
          this.showLoading();
        }
      }
    };
  }

  private static modernPiePlotOptions(borderWidth = 0): any {
    return {
      pie: {
        borderWidth,
        borderColor: '#ffffff',
        innerSize: '72%',
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: { enabled: false },
        showInLegend: true,
        shadow: false,
        states: {
          hover: {
            brightness: 0.05,
            halo: {
              size: 8,
              opacity: 0.15
            }
          },
          inactive: {
            opacity: 0.4
          }
        },
        point: {
          events: {
            mouseOver: function () {
              this.graphic?.animate({ r: this.shapeArgs.r + 4 }, { duration: 200 });
            },
            mouseOut: function () {
              this.graphic?.animate({ r: this.shapeArgs.r }, { duration: 200 });
            }
          }
        }
      }
    };
  }

  private static modernPieTooltip(): any {
    return {
      ...ChartTiktokService.baseTooltip(),
      pointFormat: '<span style="font-size:22px;font-weight:700;color:{point.color}">{point.percentage:.1f}%</span>' +
        '<br/><span style="font-size:12px;color:#94a3b8">{point.y} users</span>'
    };
  }

  private static modernBarPlotOptions(isBar = true): any {
    const key = isBar ? 'bar' : 'column';
    return {
      [key]: {
        borderRadius: 6,
        borderWidth: 0,
        groupPadding: 0.15,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: '600',
            fontFamily: ChartTiktokService.fontFamily,
            color: '#475569',
            textOutline: 'none'
          }
        },
        states: {
          hover: {
            brightness: -0.06
          },
          inactive: {
            opacity: 0.5
          }
        }
      },
      series: {
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        }
      }
    };
  }

  private static modernBarTooltip(suffix = ''): any {
    return {
      ...ChartTiktokService.baseTooltip(),
      pointFormat: `<span style="font-size:20px;font-weight:700;color:{point.color}">{point.y}${suffix}</span>`
    };
  }

  private static modernLoading(): any {
    return {
      hideDuration: 400,
      showDuration: 400,
      style: {
        position: 'absolute',
        backgroundColor: 'rgba(255,255,255,0.85)',
        textAlign: 'center',
        fontSize: '14px',
        color: '#94a3b8'
      }
    };
  }

  private static columnGradient(hex: string): any {
    return {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, hex],
        [1, ChartTiktokService.adjustBrightness(hex, -25)]
      ]
    };
  }

  private static adjustBrightness(hex: string, amount: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, ((num >> 16) & 0xff) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
    const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  // ═══════════════════════════════════════════════════════════════════
  // CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static colorPalette = {
    gender: {
      male: '#0AAEFF',
      female: '#FF72B5',
    }
  };

  public static getGenderChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Gender',
      data: []
    });
    const dataArray = {
      male: 0,
      female: 0
    };

    if (data.gender !== undefined) {
      dataArray.male = data.gender[2].percent;
      dataArray.female = data.gender[1].percent;
    }

    if (dataArray !== null) {
      series[0].data.push({
        y: dataArray.male,
        name: 'Male',
        color: '#0AAEFF',
        dataLabels: { enabled: false }
      });
      series[0].data.push({
        y: dataArray.female,
        name: 'Female',
        color: '#FF72B5',
        dataLabels: { enabled: false }
      });
    }

    return new Chart({
      chart: ChartTiktokService.baseChart('pie'),
      title: {
        text: '',
        align: 'center',
        verticalAlign: 'middle'
      },
      credits: { enabled: false },
      legend: {
        enabled: false,
        ...ChartTiktokService.baseLegend()
      },
      tooltip: ChartTiktokService.modernPieTooltip(),
      loading: ChartTiktokService.modernLoading(),
      series: series,
      plotOptions: ChartTiktokService.modernPiePlotOptions()
    });
  }

  public static getAgeAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: ChartTiktokService.columnGradient('#25CCD2')
    });

    if (data.age !== null) {
      for (const i in data.age) {
        const d = data.age[i];
        categories.push(d.key);
        series[0].data.push(d.percent);
      }
    }

    return new Chart({
      chart: ChartTiktokService.baseChart('column', { height: 260 }),
      title: { text: '' },
      xAxis: ChartTiktokService.modernXAxis(categories),
      yAxis: ChartTiktokService.modernYAxis(),
      plotOptions: ChartTiktokService.modernBarPlotOptions(false),
      tooltip: ChartTiktokService.modernBarTooltip('%'),
      legend: {
        enabled: false,
        ...ChartTiktokService.baseLegend()
      },
      credits: { enabled: false },
      series: series
    });
  }

  public static getLanguageAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#7c5ce0'], [1, '#957AED']]
      }
    });

    if (data.lang !== null) {
      // tslint:disable-next-line:forin
      for (const i in data.lang) {
        const d = data.lang[i];
        categories.push(d.key.toUpperCase());
        series[0].data.push(d.percent);
        // tslint:disable-next-line:radix
        if (parseInt(i) > 9) {
          break;
        }
      }
    }

    return new Chart({
      chart: ChartTiktokService.baseChart('bar'),
      title: { text: '' },
      xAxis: ChartTiktokService.modernXAxis(categories),
      yAxis: ChartTiktokService.modernYAxis(),
      plotOptions: ChartTiktokService.modernBarPlotOptions(true),
      tooltip: ChartTiktokService.modernBarTooltip('%'),
      legend: {
        enabled: false,
        ...ChartTiktokService.baseLegend()
      },
      credits: { enabled: false },
      series: series
    });
  }

  public static getQualityDistributionChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Percent of users',
      data: []
    });

    const dataArray = {
      verygood: 0,
      good: 0,
      medium: 0,
      bad: 0,
      verybad: 0
    };

    if (data.quality_distribution !== null) {
      dataArray.verygood = Math.round(data.quality_distribution.verygood.percent * 100) / 100;
      dataArray.good = Math.round(data.quality_distribution.good.percent * 100) / 100;
      dataArray.medium = Math.round(data.quality_distribution.medium.percent * 100) / 100;
      dataArray.bad = Math.round(data.quality_distribution.bad.percent * 100) / 100;
      dataArray.verybad = Math.round(data.quality_distribution.verybad.percent * 100) / 100;
    }

    if (dataArray !== null) {
      series[0].data.push({
        y: dataArray.verygood,
        name: 'Very good',
        color: '#2EC973',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 300 followings'
      });
      series[0].data.push({
        y: dataArray.good,
        name: 'Good',
        color: '#25CCD2',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 500 followings'
      });
      series[0].data.push({
        y: dataArray.medium,
        name: 'Medium',
        color: '#FFC212',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 1500 followings'
      });
      series[0].data.push({
        y: dataArray.bad,
        name: 'Bad',
        color: '#FF7D51',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 2500 followings'
      });
      series[0].data.push({
        y: dataArray.verybad,
        name: 'Very bad',
        color: '#FF5656',
        dataLabels: { enabled: false },
        info: 'Accounts that have more than 2500 followings'
      });
    }

    return new Chart({
      chart: ChartTiktokService.baseChart('pie', {
        events: ChartTiktokService.showLoadingOnEmpty()
      }),
      title: {
        text: '',
        align: 'center',
        verticalAlign: 'middle'
      },
      legend: {
        enabled: false,
        ...ChartTiktokService.baseLegend(),
        itemMarginBottom: 20,
        margin: 30
      },
      credits: { enabled: false },
      tooltip: ChartTiktokService.modernPieTooltip(),
      loading: ChartTiktokService.modernLoading(),
      series: series,
      plotOptions: ChartTiktokService.modernPiePlotOptions()
    });
  }
}
